import { fetchDriver } from "@/libs/api/requests";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Driver } from "../types";
import { useUser } from "./user";
import { AxiosError } from "axios";

type DriverContextType = {
  isDriverLoaded: boolean;
  driver: Driver | null;
  hasDriverError: boolean;
  resetError: () => void;
};

const DriverContext = createContext<DriverContextType | undefined>(undefined);

export function DriverProvider({ children }: { children: ReactNode }) {
  const [driver, setDriver] = useState<Driver | null>(null);
  const [isDriverLoaded, setIsDriverLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { isLoggedIn, isInitialized } = useUser();

  useEffect(() => {
    if (!isLoggedIn || !isInitialized) {
      return;
    }

    fetchDriver()
      .then((driver) => {
        if (!driver || !driver.employee) {
          return;
        }

        const nextDriver: Driver = {
          id: driver.id,
          name: driver.employee.name,
          picture: driver.employee.profilePicture,
          employeeId: driver.employee.id,
        };

        setDriver(nextDriver);
        setIsDriverLoaded(true);
      })
      .catch((error) => {
        if (
          error instanceof AxiosError &&
          error.response &&
          error.response.status === 400 &&
          error.response.data.data === "The requesting employee is not a driver"
        ) {
          setHasError(true);
        }
      });
  }, [isInitialized, isLoggedIn]);

  const values: DriverContextType = useMemo(
    () => ({
      isDriverLoaded,
      hasDriverError: hasError,
      driver,
      resetError: () => setHasError(false),
    }),
    [driver, hasError, isDriverLoaded],
  );

  return (
    <DriverContext.Provider value={values}>{children}</DriverContext.Provider>
  );
}

export function useDriver() {
  const context = useContext(DriverContext);

  if (!context) {
    throw new Error("useDriver must be used within a DriverProvider");
  }

  return context;
}
